// src/tezos/contracts.ts
import {
  getRecvForSafeSendTx,
  tezToMutez
} from "@umami/chains";
import { CustomError, SafeSendError, SafeSendErrorType } from "@umami/utils";
import axios from "axios";
async function getReceiverFromView(network, contractAddress, hash) {
  const response = await getRecvForSafeSendTx(contractAddress.pkh, network, hash);
  if (!response) {
    throw new CustomError(
      `No response from getSafeSendView for hash ${hash} on contract ${contractAddress.pkh} on network ${network.name}.`
    );
  }
  const { receiver } = response.data.value;
  if (!receiver) {
    throw new CustomError(
      `Could not extract receiver from view response for hash ${hash} on contract ${contractAddress.pkh} on network ${network.name}.`
    );
  }
  return receiver;
}
async function createClaimOp(toolkit, contract, network, contractAddress, params) {
  const claimerAddress = await toolkit.signer.publicKeyHash();
  const intendedReceiver = await getReceiverFromView(network, contractAddress, params.hash);
  if (claimerAddress !== intendedReceiver) {
    throw new CustomError("Claim failed: Claimer is not the intended receiver.");
  }
  const batch = toolkit.wallet.batch();
  batch.withContractCall(
    contract.methodsObject.claim({
      hash_: params.hash,
      secret: params.secret
    })
  );
  return batch;
}
var TezosSafeSendProvider = class {
  toolkit;
  config;
  nativeContract;
  fa12Contract;
  fa2Contract;
  constructor(config) {
    this.config = config;
  }
  setToolkit(tezos) {
    this.toolkit = tezos;
    this.nativeContract?.setToolkit(tezos);
    this.fa12Contract?.setToolkit(tezos);
    this.fa2Contract?.setToolkit(tezos);
  }
  getNativeContract() {
    if (!this.nativeContract) {
      this.nativeContract = new TezosSafeSendContractImpl(
        this.toolkit,
        this.config.contracts.native,
        this.config.network
      );
    }
    return this.nativeContract;
  }
  getFA12Contract() {
    if (!this.fa12Contract) {
      this.fa12Contract = new TezosSafeSendFA12ContractImpl(
        this.toolkit,
        this.config.contracts.fa12,
        this.config.network
      );
    }
    return this.fa12Contract;
  }
  getFA2Contract() {
    if (!this.fa2Contract) {
      this.fa2Contract = new TezosSafeSendFA2ContractImpl(
        this.toolkit,
        this.config.contracts.fa2,
        this.config.network
      );
    }
    return this.fa2Contract;
  }
  async send(params) {
    return this.getNativeContract().send(params);
  }
  async claim(params) {
    return this.getNativeContract().claim(params);
  }
  async withdraw(params) {
    return this.getNativeContract().withdraw(params);
  }
};
var TezosSafeSendContractImpl = class {
  constructor(toolkit, contractAddress, network) {
    this.contractAddress = contractAddress;
    this.network = network;
    this.toolkit = toolkit;
  }
  contract;
  toolkit;
  setToolkit(toolkit) {
    this.toolkit = toolkit;
  }
  async initialize() {
    this.contract = await this.toolkit.wallet.at(this.contractAddress.pkh);
  }
  async createSendOp(params) {
    await this.initialize();
    const amountMutez = tezToMutez(params.amount);
    const balanceMutez = params.balance ? tezToMutez(params.balance) : null;
    if (balanceMutez && balanceMutez.lt(amountMutez)) {
      throw new SafeSendError(SafeSendErrorType.INSUFFICIENT_BALANCE);
    }
    return this.toolkit.wallet.batch().withContractCall(
      this.contract.methodsObject.send({
        receiver: params.receiver.pkh,
        hash_: params.hash,
        comment: params.comment || void 0
      }),
      { amount: Number(params.amount) }
    );
  }
  async send(params) {
    try {
      const batch = await this.createSendOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(SafeSendErrorType.UNKNOWN_ERROR, `${err.message}`);
    }
  }
  async createClaimOp(params) {
    await this.initialize();
    return createClaimOp(this.toolkit, this.contract, this.network, this.contractAddress, params);
  }
  async claim(params) {
    try {
      const batch = await this.createClaimOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(SafeSendErrorType.CLAIM_ERROR, `${err.message}`);
    }
  }
  async createWithdrawOp(params) {
    await this.initialize();
    const transferParams = {
      to: this.contractAddress.pkh,
      amount: 0,
      parameter: {
        entrypoint: "withdraw",
        value: params.hash
      }
    };
    return this.toolkit.wallet.batch().withTransfer(transferParams);
  }
  async withdraw(params) {
    try {
      const batch = await this.createWithdrawOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(SafeSendErrorType.WITHDRAW_ERROR, `${err.message}`);
    }
  }
  getAddress() {
    return this.contractAddress;
  }
};
var TezosSafeSendFA12ContractImpl = class {
  contract;
  toolkit;
  contractAddress;
  network;
  constructor(toolkit, contractAddress, network) {
    this.toolkit = toolkit;
    this.contractAddress = contractAddress;
    this.network = network;
  }
  setToolkit(toolkit) {
    this.toolkit = toolkit;
  }
  getAddress() {
    return this.contractAddress;
  }
  async initialize() {
    this.contract = await this.toolkit.wallet.at(this.contractAddress.pkh);
  }
  async getAllowance(tokenContract) {
    await this.initialize();
    const tokenContractInstance = await this.toolkit.wallet.at(tokenContract.pkh);
    const owner = await this.toolkit.signer.publicKeyHash();
    const spender = this.contractAddress.pkh;
    try {
      const allowance = await tokenContractInstance.views.getAllowance(owner, spender).read();
      return allowance.toString();
    } catch (error) {
      throw new SafeSendError(
        SafeSendErrorType.UNKNOWN_ERROR,
        `Failed to get allowance: ${error.message}`
      );
    }
  }
  async createSendOp(params) {
    await this.initialize();
    const batch = this.toolkit.wallet.batch();
    const tokenContract = await this.toolkit.wallet.at(params.tokenContract.pkh);
    const currentAllowance = await this.getAllowance(params.tokenContract);
    let balance;
    if (params.balance) {
      balance = params.balance;
    } else {
      const owner = await this.toolkit.signer.publicKeyHash();
      balance = (await tokenContract.views.getBalance(owner).read()).toString();
    }
    if (BigInt(currentAllowance) > BigInt(0)) {
      batch.withContractCall(
        tokenContract.methodsObject.approve({
          spender: this.contractAddress.pkh,
          value: "0"
        })
      );
    }
    const requestedAllowance = BigInt(currentAllowance) + BigInt(params.amount);
    let newTotalAllowance = requestedAllowance > BigInt(balance) ? balance : requestedAllowance.toString();
    if (BigInt(newTotalAllowance) > BigInt(balance)) {
      newTotalAllowance = balance;
    }
    if (BigInt(newTotalAllowance) < BigInt(params.amount)) {
      throw new SafeSendError(SafeSendErrorType.INSUFFICIENT_BALANCE);
    }
    batch.withContractCall(
      tokenContract.methodsObject.approve({
        spender: this.contractAddress.pkh,
        value: newTotalAllowance
      })
    );
    batch.withContractCall(
      this.contract.methodsObject.authorize({
        receiver: params.receiver.pkh,
        hash_: params.hash,
        token_contract: params.tokenContract.pkh,
        amount: params.amount,
        comment: params.comment || void 0
      })
    );
    return batch;
  }
  async send(params) {
    try {
      const batch = await this.createSendOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.SEND_ERROR,
        `Failed to send FA1.2 safe send: ${err.message}`
      );
    }
  }
  async createClaimOp(params) {
    await this.initialize();
    return createClaimOp(this.toolkit, this.contract, this.network, this.contractAddress, params);
  }
  async claim(params) {
    try {
      const batch = await this.createClaimOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.CLAIM_ERROR,
        `Failed to claim FA1.2 safe send: ${err.message}`
      );
    }
  }
  async createWithdrawOp(params) {
    await this.initialize();
    const transferParams = {
      to: this.contractAddress.pkh,
      amount: 0,
      parameter: {
        entrypoint: "withdraw",
        value: params.hash
      }
    };
    return this.toolkit.wallet.batch().withTransfer(transferParams);
  }
  async withdraw(params) {
    try {
      const batch = await this.createWithdrawOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.WITHDRAW_ERROR,
        `Failed to withdraw FA1.2 safe send: ${err.message}`
      );
    }
  }
};
var TezosSafeSendFA2ContractImpl = class {
  constructor(toolkit, contractAddress, network) {
    this.contractAddress = contractAddress;
    this.network = network;
    this.toolkit = toolkit;
  }
  contract;
  toolkit;
  setToolkit(toolkit) {
    this.toolkit = toolkit;
  }
  async initialize() {
    this.contract = await this.toolkit.wallet.at(this.contractAddress.pkh);
  }
  getAddress() {
    return this.contractAddress;
  }
  async isOperatorSet(params) {
    try {
      const tokenContract = await this.toolkit.wallet.at(params.tokenContract.pkh);
      const owner = await this.toolkit.signer.publicKeyHash();
      const storage = await tokenContract.storage();
      if (!storage.operators) {
        return false;
      }
      const operatorKey = {
        owner,
        operator: this.contract.address,
        token_id: params.tokenId
      };
      const operatorValue = await storage.operators.get(operatorKey);
      return operatorValue !== void 0;
    } catch (err) {
      if (err.message.includes("Key not found in map")) {
        return false;
      }
      throw new SafeSendError(
        SafeSendErrorType.UNKNOWN_ERROR,
        `Failed to check operator status: ${err.message}`
      );
    }
  }
  async createSendOp(params) {
    await this.initialize();
    const batch = this.toolkit.wallet.batch();
    for (const transfer of params.transfers) {
      const isSet = await this.isOperatorSet({
        tokenContract: transfer.tokenContract,
        tokenId: transfer.tokens[0].tokenId
      });
      if (!isSet) {
        const tokenContract = await this.toolkit.wallet.at(transfer.tokenContract.pkh);
        batch.withContractCall(
          tokenContract.methodsObject.update_operators([
            {
              add_operator: {
                owner: await this.toolkit.signer.publicKeyHash(),
                operator: this.contract.address,
                token_id: transfer.tokens[0].tokenId
              }
            }
          ])
        );
      }
    }
    batch.withContractCall(
      this.contract.methodsObject.authorize({
        receiver: params.receiver.pkh,
        hash_: params.hash,
        transfers: params.transfers.map((transfer) => ({
          token_contract: transfer.tokenContract.pkh,
          tokens: transfer.tokens.map((token) => ({
            token_id: token.tokenId,
            amount: token.amount
          }))
        })),
        comment: params.comment || void 0
      })
    );
    return batch;
  }
  async send(params) {
    try {
      const batch = await this.createSendOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.SEND_ERROR,
        `Failed to send FA2 safe send: ${err.message}`
      );
    }
  }
  async createClaimOp(params) {
    await this.initialize();
    return createClaimOp(this.toolkit, this.contract, this.network, this.contractAddress, params);
  }
  async claim(params) {
    try {
      const batch = await this.createClaimOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.CLAIM_ERROR,
        `Failed to claim FA2 safe send: ${err.message}`
      );
    }
  }
  async createWithdrawOp(params) {
    await this.initialize();
    const transferParams = {
      to: this.contractAddress.pkh,
      amount: 0,
      parameter: {
        entrypoint: "withdraw",
        value: params.hash
      }
    };
    return this.toolkit.wallet.batch().withTransfer(transferParams);
  }
  async withdraw(params) {
    try {
      const batch = await this.createWithdrawOp(params);
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.WITHDRAW_ERROR,
        `Failed to withdraw FA2 safe send: ${err.message}`
      );
    }
  }
  async setOperator(params) {
    try {
      const isSet = await this.isOperatorSet(params);
      if (isSet) {
        throw new SafeSendError(SafeSendErrorType.FA2_OPERATION_ALREADY_SET);
      }
      const batch = this.toolkit.wallet.batch();
      const tokenContract = await this.toolkit.wallet.at(params.tokenContract.pkh);
      batch.withContractCall(
        tokenContract.methodsObject.update_operators([
          {
            add_operator: {
              owner: await this.toolkit.signer.publicKeyHash(),
              operator: this.contract.address,
              token_id: params.tokenId
            }
          }
        ])
      );
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.UNKNOWN_ERROR,
        `Failed to set operator: ${err.message}`
      );
    }
  }
  async removeOperator(params) {
    try {
      const isSet = await this.isOperatorSet(params);
      if (!isSet) {
        throw new SafeSendError(SafeSendErrorType.FA2_OPERATOR_NOT_SET, "Operator is not set");
      }
      const tokenContract = await this.toolkit.wallet.at(params.tokenContract.pkh);
      const batch = this.toolkit.wallet.batch();
      batch.withContractCall(
        tokenContract.methodsObject.update_operators([
          {
            remove_operator: {
              owner: await this.toolkit.signer.publicKeyHash(),
              operator: this.contract.address,
              token_id: params.tokenId
            }
          }
        ])
      );
      const op = await batch.send();
      return op;
    } catch (err) {
      throw new SafeSendError(
        SafeSendErrorType.FA2_REMOVE_OPERATOR_ERROR,
        `Failed to remove operator: ${err.message}`
      );
    }
  }
};

// src/utils.ts
import { createHash, randomBytes } from "crypto";
import { Parser, packDataBytes } from "@taquito/michel-codec";
var computeHash = (secret, sender_address, receiver_address) => {
  const p = new Parser();
  const michelsonData = `(Pair 0x${secret} (Pair "${sender_address}" "${receiver_address}"))`;
  const dataToPack = p.parseMichelineExpression(michelsonData);
  const michelsonType = "(pair bytes (pair address address))";
  const typeToPack = p.parseMichelineExpression(michelsonType);
  const { bytes: payload } = packDataBytes(dataToPack, typeToPack);
  return createHash("sha512").update(payload, "hex").digest("hex");
};
var generateSecret = () => randomBytes(32).toString("hex");

// src/safeSendHelper.ts
import {
  ChainIds,
  MAINNET,
  SHADOWNET,
  SafeSendAssetType,
  setSafeSendContracts
} from "@umami/chains";
import { CustomError as CustomError2 } from "@umami/utils";
var safeSendConfig = {
  mainnet: {
    native: { type: "contract", pkh: "" },
    fa12: { type: "contract", pkh: "" },
    fa2: { type: "contract", pkh: "" }
  },
  shadownet: {
    native: { type: "contract", pkh: "" },
    fa12: { type: "contract", pkh: "" },
    fa2: { type: "contract", pkh: "" }
  }
};
var providerCache = {};
var updateProviders = () => {
  providerCache["tezos-mainnet"] = new TezosSafeSendProvider({
    network: MAINNET,
    contracts: safeSendConfig.mainnet
  });
  providerCache["tezos-shadownet"] = new TezosSafeSendProvider({
    network: SHADOWNET,
    contracts: safeSendConfig.shadownet
  });
  setSafeSendContracts([
    {
      chainId: ChainIds.TEZOS,
      assetType: SafeSendAssetType.TEZ,
      mainnet: safeSendConfig.mainnet.native.pkh,
      shadownet: safeSendConfig.shadownet.native.pkh
    },
    {
      chainId: ChainIds.TEZOS,
      assetType: SafeSendAssetType.FA12,
      mainnet: safeSendConfig.mainnet.fa12.pkh,
      shadownet: safeSendConfig.shadownet.fa12.pkh
    },
    {
      chainId: ChainIds.TEZOS,
      assetType: SafeSendAssetType.FA2,
      mainnet: safeSendConfig.mainnet.fa2.pkh,
      shadownet: safeSendConfig.shadownet.fa2.pkh
    }
  ]);
};
var configureSafeSend = (config) => {
  safeSendConfig = config;
  updateProviders();
};
var isMainnet = (network) => network.chain === ChainIds.TEZOS && network.name.toLowerCase() === "mainnet";
var isShadownet = (network) => network.chain === ChainIds.TEZOS && network.name.toLowerCase() === "shadownet";
var isSafeSendSupported = (network) => isMainnet(network) || isShadownet(network);
var getProvider = (network) => {
  if (!isSafeSendSupported(network)) {
    throw new CustomError2(`SafeSend not supported on network ${network.name}`);
  }
  const networkKey = `${network.chain}-${network.name}`;
  return providerCache[networkKey];
};
var getContractForType = (provider, operationType) => {
  switch (operationType) {
    case SafeSendAssetType.TEZ:
      return provider.getNativeContract();
    case SafeSendAssetType.FA12:
      return provider.getFA12Contract();
    case SafeSendAssetType.FA2:
      return provider.getFA2Contract();
    default:
      throw new CustomError2(`Unsupported operation type: ${operationType}`);
  }
};
var getSafeSendAssetType = (network, sender = "", target = "") => {
  const provider = getProvider(network);
  if (target === provider.getNativeContract().getAddress().pkh) {
    return SafeSendAssetType.TEZ;
  } else if (target === provider.getFA12Contract().getAddress().pkh) {
    return SafeSendAssetType.FA12;
  } else if (target === provider.getFA2Contract().getAddress().pkh) {
    return SafeSendAssetType.FA2;
  }
  throw new CustomError2(`Unsupported operation type: ${sender + " to" + target}`);
};
var send = async (tezosToolkit, network, operation, comment, balance) => {
  const provider = getProvider(network);
  provider.setToolkit(tezosToolkit);
  const sender = await tezosToolkit.signer.publicKeyHash();
  const secret = generateSecret();
  const hash = computeHash(secret, sender, operation.recipient.pkh);
  if (comment && comment.length > 140) {
    throw new CustomError2("Comments must be less than 140 characters");
  }
  let batch;
  switch (operation.type) {
    case "tez":
      batch = await provider.getNativeContract().createSendOp({
        receiver: operation.recipient,
        amount: (Number(operation.amount) / 1e6).toString(),
        hash,
        comment,
        balance
      });
      break;
    case "fa1.2":
      batch = await provider.getFA12Contract().createSendOp({
        receiver: operation.recipient,
        amount: operation.amount,
        hash,
        tokenContract: operation.contract,
        comment,
        balance
      });
      break;
    case "fa2":
      batch = await provider.getFA2Contract().createSendOp({
        receiver: operation.recipient,
        hash,
        transfers: [
          {
            tokenContract: operation.contract,
            tokens: [{ tokenId: operation.tokenId, amount: operation.amount }]
          }
        ],
        comment
      });
      break;
    default:
      throw new CustomError2(`Unsupported operation type: ${operation}`);
  }
  return {
    batch,
    safeSendHash: hash,
    secret
  };
};
var runSend = async (tezosToolkit, network, operation, comment) => {
  const { batch, safeSendHash, secret } = await send(tezosToolkit, network, operation, comment);
  const op = await batch.send();
  return {
    operation: op,
    safeSendHash,
    secret
  };
};
var claim = async (tezosToolkit, network, hash, secret, operationType) => {
  const provider = getProvider(network);
  provider.setToolkit(tezosToolkit);
  const contract = getContractForType(provider, operationType);
  return contract.createClaimOp({
    hash,
    secret
  });
};
var runClaim = async (tezosToolkit, network, hash, secret, operationType) => {
  const batch = await claim(tezosToolkit, network, hash, secret, operationType);
  const op = await batch.send();
  return op;
};
var withdraw = async (tezosToolkit, network, hash, operationType) => {
  const provider = getProvider(network);
  provider.setToolkit(tezosToolkit);
  const contract = getContractForType(provider, operationType);
  return contract.createWithdrawOp({
    hash: { bytes: hash }
  });
};
var runWithdraw = async (tezosToolkit, network, hash, operationType) => {
  const batch = await withdraw(tezosToolkit, network, hash, operationType);
  const op = await batch.send();
  return op;
};
var estimateSafeSendAction = async (tezosToolkit, batch) => {
  const estimate = await tezosToolkit.estimate.batch(batch.operations);
  return estimate.map((e) => ({
    gasLimit: e.gasLimit,
    storageLimit: e.storageLimit,
    fee: e.totalCost
  }));
};
export {
  TezosSafeSendProvider,
  claim,
  computeHash,
  configureSafeSend,
  estimateSafeSendAction,
  generateSecret,
  getSafeSendAssetType,
  isSafeSendSupported,
  runClaim,
  runSend,
  runWithdraw,
  send,
  withdraw
};
